/** @jsxImportSource theme-ui */
import React from 'react';
import { Box, Flex, Heading, Text } from 'theme-ui';
import Button from '@/components/Button';
import {
    ArticlesExample,
    CustomerData,
    EmbedLevel,
    PricesExample,
} from '@/containers/widget/embedScript';
import { PreviewColorScheme } from '@/containers/widget/useWidgetPreview';
import { EmbedPreviewController } from './useEmbedPreviewController';

interface Props {
    controller: EmbedPreviewController;
    fitViewport: boolean;
    level: EmbedLevel;
    articlesExample: ArticlesExample;
    pricesExample: PricesExample;
    customerData: CustomerData | null;
}

const SCHEMES: PreviewColorScheme[] = ['light', 'dark'];

const HELPER_HELP_TEXT =
    'Select a service in the preview, then use “Send setArticleQuantity” on the left. Events and commands appear in the console below; rejected commands are logged in the widget iframe’s console.';

const SYNCED_HELP_TEXT =
    'Select a service in the preview, then change the quantity of one synced add-on in its checkout. This page mirrors it to the other synced add-ons; see the console below.';

const PRICES_HELP_TEXT =
    'Select a service and a time in the preview, then use “Send setPriceQuantity” on the left. Quantities are chosen on the checkout step; rejected commands are logged in the widget iframe’s console.';

const SYNCED_PRICES_HELP_TEXT =
    'Select a service with two or more price counters and a time in the preview, then change one synced price count in its checkout. This page mirrors it to the other synced prices; see the console below. A follow-up over the service maximum or the spots left is rejected in the widget iframe’s console.';

const CUSTOMER_INVALID_TEXT = 'Fix the customer fields to send setCustomerData.';

const CUSTOMER_SENT_TEXT =
    'Nothing is sent until you click “Send setCustomerData” on the left. The SDK holds the command until the widget is ready; open the booking form in the preview to see the values.';

const EmbedPreviewColumn: React.FC<Props> = ({
    controller,
    fitViewport,
    level,
    articlesExample,
    pricesExample,
    customerData,
}) => (
    <Box>
        <Flex sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Heading as="h4" sx={{ m: 0 }}>
                Preview
            </Heading>
            <Flex sx={{ gap: 2 }}>
                {SCHEMES.map((option) => (
                    <Button
                        key={option}
                        type="button"
                        variant={controller.scheme === option ? 'primary' : 'secondary'}
                        aria-pressed={controller.scheme === option}
                        onClick={() => controller.setScheme(option)}
                    >
                        {option === 'light' ? 'Light' : 'Dark'}
                    </Button>
                ))}
                <Button type="button" variant="secondary" onClick={controller.resetWidget}>
                    Reset widget
                </Button>
            </Flex>
        </Flex>
        {level === 'articles' && (
            <HelpText>
                {articlesExample === 'synced' ? SYNCED_HELP_TEXT : HELPER_HELP_TEXT}
            </HelpText>
        )}
        {level === 'prices' && (
            <HelpText>
                {pricesExample === 'synced' ? SYNCED_PRICES_HELP_TEXT : PRICES_HELP_TEXT}
            </HelpText>
        )}
        {level === 'customer' && (
            <HelpText>{customerData ? CUSTOMER_SENT_TEXT : CUSTOMER_INVALID_TEXT}</HelpText>
        )}
        <Box
            key={controller.mountKey}
            ref={controller.session.mountWidget}
            data-testid="preview-mount"
            sx={{
                minHeight: fitViewport ? '800px' : '700px',
                border: '1px solid',
                borderColor: 'gray.3',
                borderRadius: 'sm',
                overflowY: 'auto',
            }}
        />
    </Box>
);

const HelpText: React.FC = ({ children }) => (
    <Text sx={{ display: 'block', fontSize: 0, color: 'gray.6', my: 2 }}>{children}</Text>
);

export default EmbedPreviewColumn;
